import { FileWarning } from "lucide-react";
import { useCallback, useMemo } from "react";

import { getLineRange } from "~/components/get-line-range";
import { StatusCard } from "~/components/status-card";
import { useParsedJson } from "~/components/use-parsed-json";
import { JSON_FILE_TREE_DISPLAY_MAX_SIZE } from "~/constants";
import { useFileContent } from "~/hooks/use-file-content";
import { ThemedLoadingSpinner } from "~/theming/feature/themed-loading-spinner";
import { extractJsonPathAsString } from "~/utils/jsonpath";

import { CodeViewer } from "../code-viewer";
import { formatJsonAsTreeViewerText, JsonTreeViewer } from "../json-tree-viewer";
import { CopyContentButton } from "./copy-content-button";
import { FileProperties } from "./file-properties";
import { getContentType } from "./get-content-type";
import { JsonViewMode } from "./json-view-mode";
import { SelectionAction } from "./selection-action";
import { ViewerPrefs } from "./url-fragment";
import type { ViewConfig } from "./view-config";

interface TextFileViewerProps {
  content: string | Uint8Array;
  properties: FileProperties;
  versionId?: string;
  initialView?: ViewerPrefs;
  jsonViewMode?: JsonViewMode | null;
  viewConfig?: ViewConfig | null;
  lineRange?: { startLine: number; endLine: number } | null;
  onSelectionChange?: (action: SelectionAction) => void;
}

export function TextFileViewer({
  content,
  properties,
  versionId,
  initialView,
  jsonViewMode,
  viewConfig,
  lineRange,
  onSelectionChange,
}: TextFileViewerProps) {
  const contentType = getContentType(properties.extension);
  const processedContentState = useFileContent(content);

  // Determine if this is a JSON file
  const isJsonFile = useMemo(() => {
    return Boolean(
      (contentType && contentType.includes("json")) ||
      (properties.extension && properties.extension.toLowerCase() === "json"),
    );
  }, [contentType, properties.extension]);

  // Determine the effective view mode (always default to tree for JSON files)
  const effectiveJsonViewMode = useMemo(() => {
    if (!isJsonFile) return null;
    return jsonViewMode || "tree";
  }, [isJsonFile, jsonViewMode]);

  const shouldShowJsonTree = isJsonFile && effectiveJsonViewMode === "tree";
  const fileSize = properties.size;

  const shouldShowTree = shouldShowJsonTree && !!fileSize && fileSize <= JSON_FILE_TREE_DISPLAY_MAX_SIZE;

  // We want to request formatted JSON from the worker if:
  // 1. It's a JSON file
  // 2. We are NOT showing the tree view (because we'll need the text view)
  // 3. OR the file is too large for the tree view (so we'll fallback to text view)
  const shouldRequestFormattedJson = useMemo(() => {
    return isJsonFile && !shouldShowTree;
  }, [isJsonFile, shouldShowTree]);

  const parsedJsonState = useParsedJson(content, isJsonFile, shouldRequestFormattedJson);

  // Pretty-print JSON content - moved here to ensure hooks are always called in the same order
  const prettyTextContent = useMemo((): PrettyTextState => {
    // If we are showing the tree and parsing was successful, we don't need the pretty text.
    // This saves a lot of memory for large JSON files.
    if (shouldShowTree && parsedJsonState.status === "success") {
      return { status: "loading" };
    }

    // For non-JSON files, apply line range filtering to raw content
    if (!isJsonFile) {
      if (lineRange && processedContentState.status === "success") {
        const processedContent = processedContentState.value;
        return {
          status: "success",
          value: getLineRange(processedContent, lineRange),
        };
      }
      return processedContentState;
    }

    // For JSON files, parse and format, then apply line range
    if (parsedJsonState.status === "success") {
      // Apply JSONPath filtering if viewConfig.json.path is provided
      if (viewConfig?.json?.path != null && viewConfig.json.path.startsWith("$")) {
        return { status: "success", value: extractJsonPathAsString(parsedJsonState.value, viewConfig.json.path) };
      }

      // Use pre-formatted JSON from worker if available
      const prettyJson =
        parsedJsonState.formatted ||
        (lineRange
          ? formatJsonAsTreeViewerText(parsedJsonState.value)
          : JSON.stringify(parsedJsonState.value, null, 2));

      // Apply line range filtering AFTER formatting
      if (lineRange) {
        return {
          status: "success",
          value: getLineRange(prettyJson, lineRange),
        };
      }

      return { status: "success", value: prettyJson };
    }

    if (parsedJsonState.status === "loading") {
      return { status: "loading" };
    }

    // If JSON parsing fails, fall back to line range on raw content
    if (lineRange && processedContentState.status === "success") {
      return {
        status: "success",
        value: getLineRange(processedContentState.value, lineRange),
      };
    }
    return processedContentState;
  }, [shouldShowTree, isJsonFile, parsedJsonState, processedContentState, viewConfig, lineRange]);

  const jsonTreeSelection = useMemo(() => {
    if (!initialView) {
      return { type: "none" as const };
    }

    if (initialView.jsonPath) {
      return { type: "json-path" as const, path: initialView.jsonPath };
    }

    if (initialView.codeSelection) {
      return {
        type: "line-range" as const,
        startLine: initialView.codeSelection.startLine,
        endLine: initialView.codeSelection.endLine,
      };
    }

    return { type: "none" as const };
  }, [initialView]);

  // Copy always yields the full document, not the line-range/JSONPath-filtered view.
  const getCopyText = useCallback((): string => {
    if (isJsonFile && parsedJsonState.status === "success") {
      return parsedJsonState.formatted ?? JSON.stringify(parsedJsonState.value, null, 2);
    }
    if (processedContentState.status === "success") {
      return processedContentState.value;
    }
    return "";
  }, [isJsonFile, parsedJsonState, processedContentState]);

  // Copyable whenever JSON parsed cleanly (pretty JSON) or raw content is available
  // (non-JSON text, plus the invalid-JSON fallback that renders raw text).
  const canCopy = (isJsonFile && parsedJsonState.status === "success") || processedContentState.status === "success";

  // TODO: should we show some UI feedback to the user that we could not render the tree viewer
  //  (in case of parsing error or file being too large)
  if (!shouldShowTree || parsedJsonState.status === "error") {
    if (prettyTextContent.status === "loading") {
      return (
        <StatusCard
          icon={<ThemedLoadingSpinner width="24px" height="24px" />}
          title="Loading file"
          description="Preparing a preview..."
        />
      );
    }

    if (prettyTextContent.status === "error") {
      return (
        <StatusCard
          icon={<FileWarning />}
          title="Failed to load file"
          description="Failed to load the file. Please try again later."
        />
      );
    }

    return (
      <div className="group relative h-full w-full">
        {canCopy && <CopyContentButton getText={getCopyText} />}
        <CodeViewer
          text={prettyTextContent.value}
          versionId={versionId}
          initialView={initialView}
          onSelectionChange={onSelectionChange}
        />
      </div>
    );
  }

  if (parsedJsonState.status === "loading") {
    return (
      <StatusCard
        icon={<ThemedLoadingSpinner width="24px" height="24px" />}
        title="Loading file"
        description="Preparing a preview..."
      />
    );
  }
  return (
    <div className="group relative h-full w-full">
      {canCopy && <CopyContentButton getText={getCopyText} />}
      <JsonTreeViewer
        data={parsedJsonState.value}
        onSelectionChange={onSelectionChange}
        selection={jsonTreeSelection}
        fallback={() => {
          if (prettyTextContent.status === "loading") {
            return (
              <StatusCard
                icon={<ThemedLoadingSpinner width="24px" height="24px" />}
                title="Loading file"
                description="Preparing a preview..."
              />
            );
          }

          if (prettyTextContent.status === "error") {
            return (
              <StatusCard
                icon={<FileWarning />}
                title="Failed to load file"
                description="Failed to load the file. Please try again later."
              />
            );
          }

          return (
            <CodeViewer
              text={prettyTextContent.value}
              versionId={versionId}
              initialView={initialView}
              onSelectionChange={onSelectionChange}
            />
          );
        }}
      />
    </div>
  );
}

export type PrettyTextState = { status: "loading" } | { status: "success"; value: string } | { status: "error" };
